import { Link as RouterLink } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import ComicCard from '@/components/base/ComicCard';
import { SEOHead } from '@/components/base/SEO';
import { categories, hotComics, latestUpdates, featuredComics } from '@/mocks/comics';
import { siteInfo } from '@/mocks/site';

export default function Home() {
  return (
    <>
      <SEOHead
        title="全网最全最新最好看的无删减韩国漫画网站"
        description={siteInfo.description}
        keywords={siteInfo.keywords}
        canonicalUrl={siteInfo.url}
      />

      <div className="min-h-screen flex flex-col bg-background-50">
        <Navbar />

        <main className="flex-1">
          {/* Hero Section */}
          <section className="relative w-full overflow-hidden" style={{ minHeight: '520px' }}>
            <div className="absolute inset-0">
              <img
                src="https://readdy.ai/api/search-image?query=Colorful%20abstract%20artistic%20comic%20book%20style%20illustration%20with%20warm%20vibrant%20gradients%20and%20soft%20flowing%20shapes%2C%20Korean%20manhwa%20inspired%20aesthetic%2C%20clean%20dreamy%20atmosphere%2C%20no%20text%2C%20modern%20digital%20art%2C%20coral%20pink%20and%20soft%20orange%20tones%20with%20gentle%20abstract%20patterns&width=1600&height=600&seq=hero-bg-main&orientation=landscape"
                alt="韩漫网 - 全网最全无删减韩国漫画"
                className="w-full h-full object-cover object-top"
                width={1600}
                height={600}
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/50"></div>
            </div>

            <div className="relative z-10 w-full h-full flex flex-col items-center justify-center text-center px-4 py-20" style={{ minHeight: '520px' }}>
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
                全网最全最新最好看的
                <br />
                <strong className="text-primary-400">无删减韩国漫画</strong>网站
              </h1>
              <p className="text-lg md:text-xl text-white/80 max-w-2xl mb-8">
                资源齐全 · 画质优良 · 更新及时 · 免费阅读 — 您漫画收藏的理想之地
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <a href="#latest" className="px-8 py-3 rounded-md bg-primary-500 text-background-50 font-medium hover:bg-primary-600 transition-colors text-base whitespace-nowrap cursor-pointer">
                  立即阅读
                </a>
                <a href="#categories" className="px-8 py-3 rounded-md bg-background-50/20 text-white border border-white/30 hover:bg-background-50/30 transition-colors text-base whitespace-nowrap cursor-pointer">
                  浏览分类
                </a>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 mt-16 w-full max-w-3xl">
                <div className="text-center">
                  <div className="text-3xl font-bold text-white">2000+</div>
                  <div className="text-sm text-white/60 mt-1">漫画作品</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-white">500万+</div>
                  <div className="text-sm text-white/60 mt-1">注册用户</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-white">全免费</div>
                  <div className="text-sm text-white/60 mt-1">无删减阅读</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-white">日更30+</div>
                  <div className="text-sm text-white/60 mt-1">火热连载中</div>
                </div>
              </div>
            </div>
          </section>

          {/* Hot Comics Section */}
          <section className="max-w-7xl mx-auto px-4 md:px-6 py-14 md:py-20">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-950" style={{ fontFamily: 'var(--font-heading)' }}>
                  热门推荐
                </h2>
                <p className="text-foreground-500 mt-2">千万读者都在追的韩国漫画</p>
              </div>
              <RouterLink to="/category/romance" className="text-sm text-primary-500 hover:text-primary-600 font-medium whitespace-nowrap flex items-center gap-1 cursor-pointer">
                查看更多 <i className="ri-arrow-right-line"></i>
              </RouterLink>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-5" data-product-shop>
              {hotComics.map(comic => (
                <ComicCard key={comic.id} comic={comic} />
              ))}
            </div>
          </section>

          {/* Category Navigation */}
          <section id="categories" className="bg-background-100 py-14 md:py-20">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="text-center mb-10">
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-950" style={{ fontFamily: 'var(--font-heading)' }}>
                  漫画分类
                </h2>
                <p className="text-foreground-500 mt-2">按喜好找到属于你的精彩故事</p>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                {categories.map(cat => (
                  <RouterLink
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className="group flex flex-col items-center gap-3 p-6 bg-background-50 rounded-lg border border-background-200/70 hover:border-primary-300 hover:bg-background-50 transition-all duration-300 cursor-pointer"
                  >
                    <div className="w-14 h-14 md:w-16 md:h-16 flex items-center justify-center rounded-full bg-background-100 group-hover:bg-primary-100 transition-colors">
                      <i className={`${cat.icon} text-2xl text-foreground-700 group-hover:text-primary-500 transition-colors`}></i>
                    </div>
                    <span className="text-sm font-semibold text-foreground-950 whitespace-nowrap">{cat.name}</span>
                    <span className="text-xs text-foreground-500 text-center">{cat.description}</span>
                  </RouterLink>
                ))}
              </div>
            </div>
          </section>

          {/* Latest Updates */}
          <section id="latest" className="max-w-7xl mx-auto px-4 md:px-6 py-14 md:py-20">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-950" style={{ fontFamily: 'var(--font-heading)' }}>
                  最新更新
                </h2>
                <p className="text-foreground-500 mt-2">每日热更·追漫必备</p>
              </div>
              <RouterLink to="/category/romance" className="text-sm text-primary-500 hover:text-primary-600 font-medium whitespace-nowrap flex items-center gap-1 cursor-pointer">
                全部更新 <i className="ri-arrow-right-line"></i>
              </RouterLink>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-5" data-product-shop>
              {latestUpdates.slice(0, 10).map(comic => (
                <ComicCard key={comic.id} comic={comic} size="small" />
              ))}
            </div>
          </section>

          {/* Featured Banner */}
          <section className="max-w-7xl mx-auto px-4 md:px-6 pb-14 md:pb-20">
            <div className="relative rounded-lg overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=Korean%20manhwa%20reading%20atmosphere%20banner%2C%20cozy%20warm%20reading%20nook%20with%20digital%20tablet%2C%20soft%20ambient%20lighting%2C%20comfortable%20aesthetic%2C%20minimalist%20interior%20with%20warm%20coral%20and%20cream%20tones%2C%20clean%20composition&width=1400&height=360&seq=featured-banner&orientation=landscape"
                alt="随时随地畅享韩国漫画阅读体验"
                className="w-full h-[200px] md:h-[280px] object-cover object-top"
                width={1400}
                height={360}
              />
              <div className="absolute inset-0 bg-gradient-to-r from-black/50 to-transparent flex items-center">
                <div className="px-8 md:px-12">
                  <h3 className="text-2xl md:text-3xl font-bold text-white mb-2" style={{ fontFamily: 'var(--font-heading)' }}>
                    随时随地 · 畅享阅读
                  </h3>
                  <p className="text-white/80 max-w-md mb-4">
                    手机、平板、电脑多端同步，无删减高清画质，带给您极致的漫画阅读体验。
                  </p>
                  <RouterLink to="/category/romance" className="inline-block px-6 py-2.5 rounded-md bg-primary-500 text-background-50 font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer">
                    开始探索
                  </RouterLink>
                </div>
              </div>
            </div>
          </section>
        </main>

        <Footer />
        <ScrollToTop />
      </div>
    </>
  );
}